import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { HeartImg } from 'src/assets/images';

const KEYFRAMES_ID = 'pressto-heart-celebration-keyframes';

// Inject the float keyframes once per document.
function ensureKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(KEYFRAMES_ID)) return;
  const style = document.createElement('style');
  style.id = KEYFRAMES_ID;
  style.textContent = `
    @keyframes pressto-heart-float {
      0%   { transform: translate(var(--drift-x-start, 0px), 0) rotate(var(--rot-start, 0deg)) scale(0.6); opacity: 0; }
      15%  { opacity: 1; transform: translate(var(--drift-x-mid, 0px), -10vh) rotate(var(--rot-mid, 0deg)) scale(1); }
      100% { transform: translate(var(--drift-x-end, 0px), -110vh) rotate(var(--rot-end, 0deg)) scale(1); opacity: 0; }
    }
  `;
  document.head.appendChild(style);
}

type Burst = { id: string };

function FloatingHearts({ burstId }: { burstId: string }) {
  // 12 hearts per burst, randomized.
  const hearts = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        id: `${burstId}-${i}`,
        leftPct: 10 + Math.random() * 80,
        size: 48 + Math.random() * 56,
        duration: 2.2 + Math.random() * 1.4,
        delay: Math.random() * 0.35,
        driftXStart: (Math.random() - 0.5) * 40,
        driftXMid: (Math.random() - 0.5) * 120,
        driftXEnd: (Math.random() - 0.5) * 240,
        rotStart: -20 + Math.random() * 40,
        rotMid: -25 + Math.random() * 50,
        rotEnd: -35 + Math.random() * 70,
      })),
    [burstId],
  );

  return (
    <>
      {hearts.map((h) => (
        <img
          key={h.id}
          src={HeartImg}
          alt=""
          style={{
            position: 'fixed',
            left: `${h.leftPct}%`,
            bottom: '-120px',
            width: `${h.size}px`,
            height: 'auto',
            pointerEvents: 'none',
            willChange: 'transform, opacity',
            animation: `pressto-heart-float ${h.duration}s cubic-bezier(.22,.61,.36,1) ${h.delay}s forwards`,
            ['--drift-x-start' as any]: `${h.driftXStart}px`,
            ['--drift-x-mid' as any]: `${h.driftXMid}px`,
            ['--drift-x-end' as any]: `${h.driftXEnd}px`,
            ['--rot-start' as any]: `${h.rotStart}deg`,
            ['--rot-mid' as any]: `${h.rotMid}deg`,
            ['--rot-end' as any]: `${h.rotEnd}deg`,
          }}
        />
      ))}
    </>
  );
}

export default function HeartCelebration({ bursts }: { bursts: Burst[] }) {
  useEffect(() => {
    ensureKeyframes();
  }, []);

  if (typeof document === 'undefined' || bursts.length === 0) {
    return null;
  }

  return createPortal(
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 9999,
        overflow: 'hidden',
      }}
    >
      {bursts.map((b) => (
        <FloatingHearts key={b.id} burstId={b.id} />
      ))}
    </div>,
    document.body,
  );
}
